import { useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import {
  consultOrderChange,
  decideOrderChange,
  markOrderChangeEntered,
  notifyOrderChangesChanged,
  toErrorMessage,
  withdrawOrderChange,
  type OrderChange,
  type OrderChangeApproval,
} from './orderChangesApi';

/**
 * Jedna zmiana zlecenia „było → ma być” z czynnościami, na które pozwala API
 * (`can`): decyzja z powodem, „Wprowadzone w TMS”, wycofanie. Ta sama na
 * karcie zlecenia i na liście „Zmiany zleceń” (makieta back office v2, fala B,
 * B9; ścieżki MIX-02, ADM-04).
 */

/** Najkrótszy powód decyzji i wycofania — jak w API. */
export const MIN_REASON = 10;

export const changeStatusColor = (
  status: string,
): 'warning' | 'info' | 'success' | 'error' | 'default' =>
  status === 'pending'
    ? 'warning'
    : status === 'accepted' || status === 'entered'
      ? 'info'
      : status === 'closed'
        ? 'success'
        : status === 'rejected'
          ? 'error'
          : 'default';

const approvalText = (label: string, a: OrderChangeApproval | null) =>
  a
    ? `${label}: ${[a.by, dateTimeNoSeconds(a.at)].filter(Boolean).join(', ')}${a.comment ? ` — ${a.comment}` : ''}`
    : null;

type Action = 'accept' | 'reject' | 'enter' | 'withdraw' | 'consult';

const ACTION_TEXT: Record<Action, { title: string; label: string; confirm: string; required: boolean; hint: string }> = {
  accept: {
    title: 'Akceptuj zmianę',
    label: 'Powód decyzji',
    confirm: 'Akceptuj',
    required: true,
    hint: 'Twoja zgoda liczy się za strony, które reprezentujesz. Do TMS nic nie wychodzi — po komplecie zgód zmianę wprowadza przyjęcie zleceń.',
  },
  reject: {
    title: 'Odrzuć zmianę',
    label: 'Powód odrzucenia',
    confirm: 'Odrzuć',
    required: true,
    hint: 'Zgłaszający zobaczy powód przy zmianie i w historii zlecenia.',
  },
  enter: {
    title: 'Wprowadzone w TMS',
    label: 'Komentarz',
    confirm: 'Oznacz jako wprowadzone',
    required: false,
    hint: 'To Twoje oświadczenie, że zmiana jest wpisana w TMS. Zmianę zamknie import z TMS, gdy pokaże wartość „ma być”; uwag import nie porównuje — zamyka je to oświadczenie.',
  },
  consult: {
    title: 'Dodaj uwagę do zmiany',
    label: 'Uwaga',
    confirm: 'Dodaj uwagę',
    required: true,
    hint: 'Uwaga osoby realizującej to konsultacja, nie zgoda — zmianę akceptuje właściciel pola. Uwaga trafia do zmiany i do historii zlecenia.',
  },
  withdraw: {
    title: 'Wycofaj zgłoszenie',
    label: 'Powód wycofania',
    confirm: 'Wycofaj',
    required: true,
    hint: 'Zgłoszenie zostanie w historii jako wycofane.',
  },
};

export const OrderChangeItem = ({
  change,
  onChanged,
  showOrder = false,
}: {
  change: OrderChange;
  onChanged: () => void;
  showOrder?: boolean;
}) => {
  const notify = useNotify();
  const [action, setAction] = useState<Action | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const spec = action ? ACTION_TEXT[action] : null;

  const run = () =>
    void (async () => {
      if (!action) return;
      setBusy(true);
      try {
        if (action === 'accept' || action === 'reject') {
          await decideOrderChange(change.id, action, text);
        } else if (action === 'enter') {
          await markOrderChangeEntered(change.id, text);
        } else if (action === 'consult') {
          await consultOrderChange(change.id, text);
        } else {
          await withdrawOrderChange(change.id, text);
        }
        notify('Zapisano', { type: 'success' });
        setAction(null);
        setText('');
        notifyOrderChangesChanged();
        onChanged();
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Box
      data-testid="order-change"
      data-status={change.status}
      data-field={change.field}
      sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}
    >
      <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip size="small" color={changeStatusColor(change.status)} label={change.statusLabel} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          nr {change.id} · {change.fieldLabel}
        </Typography>
        {showOrder ? (
          <Typography variant="body2" color="text.secondary">
            {change.orderNumber ?? `#${change.userOfferId}`}
          </Typography>
        ) : null}
        {!change.comparedByImport ? (
          <Chip size="small" variant="outlined" label="import nie porównuje" />
        ) : null}
        {/* Właściciel pola (21.10.2026, `orderChangeFieldOwner`). */}
        {change.owner ? (
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            data-testid="order-change-owner"
            data-owner={change.owner.side}
            label={`akceptuje: ${change.owner.label}${change.owner.working ? ' (wartość robocza)' : ''}`}
          />
        ) : null}
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="order-change-values">
        było: <del>{change.previousValue ?? 'brak danych'}</del> → ma być: <strong>{change.requestedValue}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p">
        {change.originLabel} · {change.reason} ·{' '}
        {[change.requestedByName, dateTimeNoSeconds(change.requestedAt)].filter(Boolean).join(', ')}
      </Typography>
      {change.decisionRuleLabel ? (
        <Typography variant="caption" color="text.secondary" component="p">
          {change.decisionRuleLabel}
          {change.waitingFor.length > 0 ? ` — czeka na: ${change.waitingFor.join(', ')}` : ''}
        </Typography>
      ) : null}
      {[
        approvalText('Zgoda CS', change.csApproval),
        approvalText('Zgoda realizującego', change.executionApproval),
        approvalText('Zgoda Administracji', change.administrationApproval ?? null),
        approvalText('Wprowadzone w TMS', change.entered),
      ]
        .filter((t): t is string => t !== null)
        .map((t) => (
          <Typography key={t} variant="caption" color="text.secondary" component="p">
            {t}
          </Typography>
        ))}
      {(change.consultations ?? []).length > 0 ? (
        <Box data-testid="order-change-consultations" sx={{ mt: 0.5 }}>
          {(change.consultations ?? []).map((c, i) => (
            <Typography key={`${c.at}-${i}`} variant="caption" color="text.secondary" component="p">
              Uwaga realizującego ({[c.byName, dateTimeNoSeconds(c.at)].filter(Boolean).join(', ')}): {c.text}
            </Typography>
          ))}
        </Box>
      ) : null}
      {change.rejection ? (
        <Typography variant="caption" color="error" component="p">
          {change.status === 'withdrawn' ? 'Wycofane' : 'Odrzucone'}: {change.rejection.reason}
          {change.rejection.by ? ` (${change.rejection.by})` : ''}
        </Typography>
      ) : null}
      {change.closed ? (
        <Typography variant="caption" color="success.main" component="p">
          {change.closed.note}
        </Typography>
      ) : null}
      {change.reminder ? (
        <Alert severity="warning" sx={{ mt: 0.5 }} data-testid="order-change-reminder">
          {change.reminder.text}
        </Alert>
      ) : null}
      {/* Po wyjeździe na załadunek zgodę daje Team Leader działu, w CS i Administracji — każdy z działu (S-25.10-03, 26.10.2026). */}
      {change.leadApproval ? (
        <Alert severity="info" sx={{ mt: 0.5 }} data-testid="order-change-lead-approval">
          Pojazd wyruszył na załadunek — zmianę zatwierdza:{' '}
          <strong>{change.leadApproval.leads.join(', ')}</strong>. Zmiana czeka na tę decyzję.{' '}
          {change.leadApproval.reason}
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        {change.can.decide ? (
          <>
            {/* Identyfikator z kontraktu paczki (cs-/adm-zmiana-akceptuj) na opakowaniu — nasz zostaje na przycisku. */}
            {/* `data-contract-wrapper` — pomiar użycia zapisuje identyfikator kontraktu z opakowania (S-22.10-02). */}
            <span data-testid={change.owner ? (change.owner.side === 'cs' ? 'cs-zmiana-akceptuj' : 'adm-zmiana-akceptuj') : undefined} data-contract-wrapper="">
              <Button size="small" variant="contained" color="success" data-testid="order-change-accept" onClick={() => setAction('accept')}>
                Akceptuj
              </Button>
            </span>
            <Button size="small" variant="outlined" color="error" data-testid="order-change-reject" onClick={() => setAction('reject')}>
              Odrzuć
            </Button>
          </>
        ) : null}
        {change.can.enter ? (
          <Button size="small" variant="contained" data-testid="order-change-entered" onClick={() => setAction('enter')}>
            Wprowadzone w TMS
          </Button>
        ) : null}
        {change.can.consult ? (
          <Button size="small" variant="outlined" data-testid="order-change-consult" onClick={() => setAction('consult')}>
            Dodaj uwagę
          </Button>
        ) : null}
        {change.can.withdraw ? (
          <Button size="small" variant="text" data-testid="order-change-withdraw" onClick={() => setAction('withdraw')}>
            Wycofaj
          </Button>
        ) : null}
      </Stack>
      <Dialog open={spec !== null} onClose={busy ? undefined : () => setAction(null)} fullWidth maxWidth="sm">
        <DialogTitle>{spec?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{spec?.hint}</DialogContentText>
          <TextField
            label={spec?.label}
            multiline
            minRows={2}
            fullWidth
            value={text}
            disabled={busy}
            onChange={(e) => setText(e.target.value)}
            helperText={spec?.required ? `Co najmniej ${MIN_REASON} znaków` : 'Opcjonalnie'}
            slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'order-change-comment' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            disabled={busy || (!!spec?.required && text.trim().length < MIN_REASON)}
            onClick={run}
            // Kontrakt `data-testid` paczki (S-22.10-02): zatwierdzenie okna.
            data-testid="dlg-ok"
          >
            {spec?.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
